import { Calendar } from 'lucide-react';
import { useEffect } from 'react';
const CandidateDetailsSection = ({ 
    candidateDetails = {}, 
    onScheduleInterview = () => {}, 
    onSendChatbotInvite = () => {} 
}) => {
    const isRejected = candidateDetails.candidateStatus?.overall === 'rejected';


    return (
        <div className="space-y-6 max-w-full">
            {/* Top Row - Name/Status and Hireon Score Side by Side */}
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left Panel - Name and Status */}
                <div className="w-full md:w-1/2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="mb-4">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Name: {candidateDetails.candidateName}
                        </h2>
                    </div>

                    <div className={`p-4 rounded-lg border-l-4 ${
                        candidateDetails.candidateStatus?.color === 'red' ? 'bg-red-50 border-red-400' :
                        candidateDetails.candidateStatus?.color === 'yellow' ? 'bg-yellow-50 border-yellow-400' :
                        candidateDetails.candidateStatus?.color === 'blue' ? 'bg-blue-50 border-[#275F97]' :
                        candidateDetails.candidateStatus?.color === 'green' ? 'bg-blue-50 border-[#275F97]' :
                        'bg-gray-50 border-gray-400'
                    }`}>
                        <div className="font-medium text-md mb-2 text-[#275F97]">
                            Status: {candidateDetails.candidateStatus?.display}
                        </div>
                        <div className="text-sm text-[#275F97]">
                            {candidateDetails.candidateStatus?.message}
                        </div>
                    </div>
                </div>

                {/* Right Panel - Hireon Score */}
                <div className="w-full md:w-1/2 bg-[#ffffff] rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Hireon Score
                        </h3>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        {/* Circular Progress */}
                        <div className="flex-shrink-0">
                            <div className="relative w-24 h-24">
                                <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 120 120">
                                    <circle
                                        cx="60"
                                        cy="60"
                                        r="50"
                                        stroke="#ffffff"
                                        strokeWidth="8"
                                        fill="none"
                                    />
                                    <circle
                                        cx="60"
                                        cy="60"
                                        r="50"
                                        stroke={
                                            candidateDetails.candidateStatus?.color === 'red' ? '#DC2626' :
                                            candidateDetails.candidateStatus?.color === 'yellow' ? '#EAB308' :
                                            candidateDetails.candidateStatus?.color === 'blue' ? '#275F97' : 
                                            '#275F97'
                                        }
                                        strokeWidth="8"
                                        fill="none"
                                        strokeDasharray={`${(candidateDetails.scoreInfo?.value || 0) * 3.14} 314`}
                                        strokeLinecap="round"
                                    />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-xl font-bold text-gray-900">
                                        {candidateDetails.candidateStatus?.overall === 'rejected' ? '0%' :
                                         candidateDetails.candidateStatus?.overall === 'under_review' ? '0%' :
                                         candidateDetails.scoreInfo?.value ? `${candidateDetails.scoreInfo.value}%` : '85%'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons and Status */}
                        <div className="flex min-w-0">
                            {/* Send Chatbot Interview Link */}
                            {candidateDetails.actions?.canSendInvite && (
                                <button
                                    onClick={onSendChatbotInvite}
                                    className="w-full px-4 py-2 bg-[#275F97] text-[#ffff] rounded-lg font-medium flex items-center justify-center gap-2 transition-colors text-sm"
                                >
                                    <Calendar className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">Send Chatbot Interview Link</span>
                                </button>
                            )}

                            {/* Schedule Human Interview */}
                            {(candidateDetails.actions?.canScheduleHumanInterview || candidateDetails.candidateStatus?.hasFullData) && (
                                <button
                                    onClick={onScheduleInterview}
                                    className="w-full px-4 py-2 bg-[#275F97] text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors text-sm"
                                >
                                    <Calendar className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">Schedule Teams Interview</span>
                                </button>
                            )}

                            {/* Status Messages */}
                            {candidateDetails.candidateStatus?.overall === 'interview_sent_pending' && (
                                <div className="text-xs text-gray-600 text-center px-2">
                                    Interview link sent. Waiting for response.
                                </div>
                            )}

                            {candidateDetails.candidateStatus?.overall === 'rejected' && (
                                <div className="text-xs text-red-600 font-medium text-center px-2">
                                    Candidate rejected
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Candidate Information - Redesigned with Cards */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Candidate Information</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Email Card */}
                    <div className="bg-white rounded-lg p-4 border-2 border-gray-100 hover:border-[#275F97] transition-colors">
                        <div className="text-md font-medium text-[#275F97] mb-1">Email</div>
                        <div className="text-sm text-gray-800 break-all">{candidateDetails.candidateEmail}</div>
                    </div>

                    {/* Hireon Resume Score Card */}
                    <div className="bg-white rounded-lg p-4 border-2 border-gray-100 hover:border-[#275F97] transition-colors">
                        <div className="text-md font-medium text-[#275F97] mb-1">Hireon Resume Score</div>
                        <div className="text-lg font-bold text-gray-900">{candidateDetails.initialScreening?.aiScore}%</div>
                    </div>

                    {/* Skills Match Card */}
                    <div className="bg-white rounded-lg p-4 border-2 border-gray-100 hover:border-[#275F97] transition-colors">
                        <div className="text-md font-medium text-[#275F97] mb-1">Skills Match Score</div>
                        <div className="text-lg font-bold text-gray-900">{candidateDetails.initialScreening?.skillsMatch}%</div>
                    </div>
                </div>

                {/* Skills breakdown - only show for non-rejected candidates */}
                {!isRejected && (
                    <div className="mt-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Matched Skills Card */}
                            <div className="bg-white rounded-lg p-4 border-2 border-gray-100 hover:border-[#275F97] transition-colors">
                                <div className="text-md text-[#275F97] font-medium text-green-600 mb-3">Matched Skills</div>
                                <div className="flex flex-wrap gap-2">
                                    {candidateDetails.initialScreening?.matchedSkills?.map((skill, index) => (
                                        <span key={index} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Missing Skills Card */}
                            <div className="bg-white rounded-lg p-4 border-2 border-gray-100 hover:border-[#275F97] transition-colors">
                                <div className="text-md font-medium text-[#275F97] mb-3">Missing Skills</div>
                                <div className="flex flex-wrap gap-2">
                                    {candidateDetails.initialScreening?.missingSkills?.map((skill, index) => (
                                        <span key={index} className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                            {skill || 'N/A'}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Chat Response Section - Redesigned with 3 cards per row */}
            {candidateDetails.candidateStatus?.hasFullData && !isRejected && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Chat Response</h3>
                    </div>

                    <div className="space-y-4">
                        {/* Row 1: Name, Current Company, Notice Period */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="bg-white rounded-lg p-4 border-2 border-gray-100 hover:border-[#275F97] transition-colors">
                                <div className="text-md font-medium text-[#275F97] mb-1">Current Company</div>
                                <div className="text-sm font-medium text-gray-900 break-words">
                                    {candidateDetails.detailedQuestions?.hrQuestions?.find(q => q.type === 'employment_status')?.answerText}
                                </div>
                            </div>

                            <div className="bg-white rounded-lg p-4 border-2 border-gray-100 hover:border-[#275F97] transition-colors">
                                <div className="text-md font-medium text-[#275F97] mb-1">Notice Period</div>
                                <div className="text-sm font-medium text-gray-900 break-words">
                                    {candidateDetails.detailedQuestions?.hrQuestions?.find(q => q.type === 'notice_period')?.answerText || '30 Days'}
                                </div>
                            </div>

                             <div className="bg-white rounded-lg p-4 border-2 border-gray-100 hover:border-[#275F97] transition-colors">
                                <div className="text-md font-medium text-[#275F97] mb-1">Expected Salary</div>
                                <div className="text-sm font-medium text-gray-900 break-words">
                                    {candidateDetails.detailedQuestions?.hrQuestions?.find(q => q.type === 'salary')?.answerText || '12 LPA'}
                                </div>
                            </div>
                        </div>

                        {/* Row 2: Skills, Expected Salary, Availability */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                           

                            <div className="bg-white rounded-lg p-4 border-2 border-gray-100 hover:border-[#275F97] transition-colors">
                                <div className="text-md font-medium text-[#275F97] mb-1">Availability for Interview</div>
                                <div className="text-sm font-medium text-gray-900 break-words">
                                    {candidateDetails.detailedQuestions?.hrQuestions?.find(q => q.type === 'availability')?.answerText || 'Immediate'}
                                </div>
                            </div>
                
                        </div>
                    </div>
                </div>
            )}

            {/* Technical Assessment Panel - Same as before */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-96">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {candidateDetails.candidateStatus?.hasFullData ? 'Technical Assessment' : 'Assessment Status'}
                    </h3>
                </div>
                <hr />
                {candidateDetails.candidateStatus?.hasFullData ? (
                    <>
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-md font-bold text-gray-600 mt-4">Feedback</span>
                            </div>
                            <div className="text-sm text-gray-600 mb-4 break-words">
                                {candidateDetails.chatbotInterview?.feedback || 'Candidate demonstrates clear basic communication and solid technical competency. Provided detailed technical examples and explanations. Shows good potential for the software developer role.'}
                            </div>
                        </div>
                        <hr />

                        <div className="space-y-4">
                            <h4 className="font-bold text-md text-gray-600 mt-4">Technical Questions & Responses</h4>
                            {candidateDetails.detailedQuestions?.technicalQuestions?.map((question, index) => (
                                <div key={index} className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                                    <div className="mb-3">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-md font-medium text-[#275F97]">
                                                Technical Question {index + 1}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {question.difficulty} • {question.skillTested || 'Technical Skills'}
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-700 mb-3 italic break-words">
                                            "{question.questionText || 'sample question text'}"
                                        </div>
                                    </div>
                                    <div className="bg-white p-3 rounded border border-gray-200">
                                        <div className="text-sm text-gray-800 mb-2 break-words">
                                            <strong>Answer:</strong> {question.answerText || 'sample answer text'}
                                        </div>
                                        <div className="text-xs text-gray-600 mb-2 break-words">
                                            <strong>AI Assessment:</strong> {question.aiAssessment || 'Good understanding demonstrated'}
                                        </div>
                                       
                                    </div>
                                </div>
                            )) || null}
                        </div>
                    </>
                ) : (
                    <div className="text-center py-8">
                        <div className="text-gray-600 mb-4 break-words">
                            {candidateDetails.chatbotInterview?.reason || 'Technical assessment score not available yet'}
                        </div>

                        {candidateDetails.candidateStatus?.overall === 'under_review' && (
                            <div className="bg-yellow-50 p-4 rounded-lg text-sm text-yellow-800 break-words">
                                This candidate needs to be reviewed and approved before technical assessment can begin.
                            </div>
                        )}

                        {candidateDetails.candidateStatus?.overall === 'rejected' && (
                            <div className="bg-red-50 p-4 rounded-lg text-sm text-red-800 break-words">
                                The candidate has been rejected. No further assessment will be conducted.
                            </div>
                        )}

                        {candidateDetails.candidateStatus?.overall === 'shortlisted_interview_ready' && (
                            <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800 break-words">
                                Interview questions are ready. Send the chatbot interview link to begin assessment.
                            </div>
                        )}

                        {candidateDetails.candidateStatus?.overall === 'interview_sent_pending' && (
                            <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800 break-words">
                                Screening link sent successfully! Waiting for candidate to begin their assessment.
                            </div>
                        )}

                        {candidateDetails.candidateStatus?.overall === 'interview_in_progress' && (
                            <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800 break-words">
                                <div className="font-medium mb-2">Interview In Progress</div>
                                <div className="text-xs">
                                    Progress: {candidateDetails.chatbotInterview?.progress?.questionsAnswered || 0}/{candidateDetails.chatbotInterview?.progress?.totalQuestions || 0} questions
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CandidateDetailsSection;